import React, { useState, useEffect, useRef } from 'react';

import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip
} from 'chart.js/auto';
import 'chartjs-adapter-luxon';
import { DateTime } from 'luxon';

const apiUrl = process.env.REACT_APP_API_URL;
// Register required Chart.js components
Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip
);

const CryptoChart = ({ coinId }) => {
  const chartRef = useRef(null);
  const [currentMetric, setCurrentMetric] = useState('prices');
  const [timeframe, setTimeframe] = useState('7d');
  const [chartInstance, setChartInstance] = useState(null);

  // Timeframe to days mapping
  const timeframeMap = {
    '24h': 1,
    '7d': 7,
    '1m': 30,
    '3m': 90,
    '1y': 365,
    'max': 'max'
  };

  // Define getTimeUnit function
  const getTimeUnit = (timeframe) => {
    const days = timeframeMap[timeframe] || 7;
    if (days === 1) return 'hour';
    if (days <= 7) return 'day';
    if (days <= 30) return 'week';
    if (days <= 365) return 'month';
    return 'year';
  };

  useEffect(() => {
    fetchChartData();
    return () => {
      if (chartInstance) {
        chartInstance.destroy();
      }
    };
  }, [coinId, timeframe, currentMetric]);

  const fetchChartData = async () => {
    try {
      let url;
      const days = timeframeMap[timeframe] || 7;
      
      url = `${apiUrl}/get-coinchart-data?coinId=${coinId}&days=${days}`
      
      const response = await fetch(url);
      const data = await response.json();
      renderChart(data[currentMetric]);
    } catch (error) {
      console.error('Error fetching chart data:', error);
    }
  };

  const renderChart = (data) => {
    if (chartInstance) {
      chartInstance.destroy();
    }

    const ctx = chartRef.current.getContext('2d');
    const newChart = new Chart(ctx, {
      type: 'line',
      data: {
        datasets: [{
          label: currentMetric === 'prices' ? 'Price (USD)' : 'Market Cap (USD)',
          data: data.map(item => ({
            x: item[0],
            y: item[1]
          })),
          borderColor: '#00C853',
          backgroundColor: 'rgba(0, 200, 83, 0.1)',
          borderWidth: 2,
          fill: true,
          tension: 0.1,
          pointRadius: 0,
          pointHoverRadius: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: (context) => {
                return `${currentMetric === 'prices' ? 'Price' : 'Market Cap'}: $${context.parsed.y.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}`;
              }
            }
          }
        },
        scales: {
          x: {
            type: 'time',
            time: {
              unit: getTimeUnit(timeframe),
              tooltipFormat: 'MMM d, yyyy',
              displayFormats: {
                hour: 'HH:mm',
                day: 'd MMM',
                week: 'd MMM',
                month: 'MMM yyyy',
                year: 'yyyy'
              }
            },
            grid: {
              display: false,
              drawBorder: false
            },
            ticks: {
              autoSkip: true,
              maxTicksLimit: 7
            }
          },
          y: {
            grid: {
              drawBorder: false,
              color: '#f0f0f0'
            },
            ticks: {
              callback: (value) => {
                return `$${value >= 1000 ? (value/1000).toFixed(0) + 'K' : value.toFixed(0)}`;
              },
              maxTicksLimit: 8
            }
          }
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false
        },
        elements: {
          line: {
            tension: 0.1
          }
        }
      }
    });

    setChartInstance(newChart);
  };

  const handleTabClick = (newTimeframe, metric = 'prices') => {
    if (newTimeframe === 'tradingview') {
      window.open('https://www.tradingview.com/chart/', '_blank');
      return;
    }
    if (newTimeframe === 'log') {
      // Toggle log scale would require chart update
      return;
    }
    if (newTimeframe === 'price') {
      setCurrentMetric('prices');
      setTimeframe('7d');
      return;
    }
    if (newTimeframe === 'market_cap') {
      setCurrentMetric('market_caps');
      setTimeframe('7d');
      return;
    }
    setTimeframe(newTimeframe);
  };

  return (
    <div className="dashboard">
      <div className="tabs">
        <button 
          className={`tab ${currentMetric === 'prices' && timeframe === '7d' ? 'active' : ''}`}
          onClick={() => handleTabClick('price')}
        >
          Price
        </button>
        <button 
          className={`tab ${currentMetric === 'market_caps' ? 'active' : ''}`}
          onClick={() => handleTabClick('market_cap')}
        >
          Market Cap
        </button>
        <button className="tab" onClick={() => handleTabClick('tradingview')}>
          TradingView
        </button>
        {/* <button className="tab" onClick={() => handleTabClick('l2')}>
          L2
        </button> */}
        <button 
          className={`tab ${timeframe === '24h' ? 'active' : ''}`}
          onClick={() => handleTabClick('24h')}
        >
          24h
        </button>
        <button 
          className={`tab ${timeframe === '7d' ? 'active' : ''}`}
          onClick={() => handleTabClick('7d')}
        >
          7d
        </button>
        <button 
          className={`tab ${timeframe === '1m' ? 'active' : ''}`}
          onClick={() => handleTabClick('1m')}
        >
          1m
        </button>
        <button 
          className={`tab ${timeframe === '3m' ? 'active' : ''}`}
          onClick={() => handleTabClick('3m')}
        >
          3m
        </button>
        <button 
          className={`tab ${timeframe === '1y' ? 'active' : ''}`}
          onClick={() => handleTabClick('1y')}
        >
          1y
        </button>
      </div>
      
      <div className="chart-container">
        <canvas ref={chartRef} />
      </div>
    </div>
  );
};

export default CryptoChart;